import { downloadCSV } from "ra-core";
import { logError } from "../../../utils/appUtils";
import {
  FilePaths,
  Orientations,
  OutboundStatus,
} from "../../../utils/constants";
import { buildHeader, fillDownRows } from "../../../utils/exportsUtils";
import {
  generateAndDownloadPdf,
  generateFileName,
} from "../../../utils/fileUtils";
import jsonExport from "jsonexport/dist";
import {
  formatDate,
  getFormattedStartAndEndDate,
} from "../../../utils/dateUtils";
import { exportCustomField, formatCurrency } from "../../../utils/utils";
import { getTotalInvoiced } from "../invoices/InvoiceUtils";

export const outboundExportPdf = async (
  data,
  inputStartDate,
  inputEndDate,
  notify,
  filter,
) => {
  try {
    const reloadYardId = window.appUser?.reloadYardId;

    const { formattedStartDate, formattedEndDate } =
      getFormattedStartAndEndDate(
        data,
        inputStartDate,
        inputEndDate,
        "endDateTime",
      );

    const items = data.map(
      ({
        poNumber = "--",
        shipTo,
        pickupNumber = "--",
        bolNumber = "--",
        endDateTime,
        invoice,
        invoicedServices,
        carrierName = "--",
        accountName = "--",
      }) => {
        const consignee = shipTo?.name || "--";
        const shipDate = endDateTime ? formatDate(new Date(endDateTime)) : "--";
        const invoiceNumber = invoice?.invoiceNumber || "--";
        const computedTotal = getTotalInvoiced({ invoicedServices });

        return {
          orderNumber: poNumber,
          accountName,
          consignee,
          pickupNumber,
          invoiceNumber,
          // The column keeps its `carrier` key — it is the PDF column source
          // below — but reads carrierName, the field the forms bind. The
          // legacy `carrier` scalar is not in the list query's selection, so
          // this column had been exporting "--" for every row.
          carrier: carrierName,
          bolNumber,
          shipDate,
          amountBilled: computedTotal ? formatCurrency(computedTotal) : "--",
          total: computedTotal || 0,
        };
      },
    );

    // Calculate display flags
    const showAmounts =
      filter?.outboundShipmentByReloadYardByStatusByDates
        ?.statusEndDateTimeArrivalDateTimeScheduledDateTimeCreateDateTime
        ?.beginsWith?.status === OutboundStatus.RELEASED ||
      filter?.searchOutboundShipments?.status?.eq === OutboundStatus.RELEASED;

    const showShipDateColumn = !items.every(
      ({ shipDate }) => shipDate === "--",
    );

    // Calculate totals
    const totalAmountBilled = items.reduce(
      (sum, { amountBilled }) => sum + (amountBilled || 0),
      0,
    );

    // Generate file names
    const fileName = generateFileName({ resource: "outbound", ext: "pdf" });
    const downloadName = generateFileName({
      resource: "outbound",
      ext: "pdf",
      includeDate: true,
    });

    // Build columns array
    const columns = [
      { label: "Order #", source: "orderNumber" },
      { label: "Consignee", source: "consignee" },
      { label: "Pickup #", source: "pickupNumber" },
      { label: "Carrier", source: "carrier" },
      { label: "BOL #", source: "bolNumber" },
    ];

    if (showShipDateColumn) {
      columns.push({ label: "Shipped", source: "shipDate" });
    }

    if (showAmounts) {
      columns.push(
        { label: "Invoice #", source: "invoiceNumber" },
        { label: "Invoiced", source: "total" },
      );
    }

    if (!filter?.accountId?.eq) {
      columns.unshift({ label: "Customer", source: "accountName" });
    }

    // Build header
    const header = buildHeader(
      data,
      formattedStartDate,
      formattedEndDate,
      filter,
    );

    // Build footer
    const footer = [
      {
        label: "Total Loads",
        value: data.length,
      },
    ];

    if (showAmounts) {
      footer.unshift({
        label: "Total Invoiced",
        value: formatCurrency(totalAmountBilled) || "",
      });
    }

    // Generate the PDF report
    const templateKey = `${FilePaths.TEMPLATES_PDF}/${FilePaths.TEMPLATE_REPORT}`;
    const outputFileKey = `${reloadYardId}/${reloadYardId}/${FilePaths.INVENTORY}/${fileName}`;

    await generateAndDownloadPdf({
      templateKey,
      outputFileKey,
      downloadName,
      data: {
        metaData: {
          orientation: Orientations.PORTRAIT,
          currentDate: new Date().toLocaleString(),
        },
        content: {
          header,
          title: "OUTBOUND LOAD REPORT",
          table: {
            columns,
            items,
          },
          footer,
        },
      },
    });
  } catch (err) {
    logError("outbound:exportPdf", err, notify);
  }
};

export const outboundExportCsv = (outbounds) => {
  const customHeaderMap = new Map();
  debugger;
  const outboundsForExport = outbounds.map((outbound) => {
    const outboundForExport = { ...outbound };

    if (outbound.shipmentLineItems.items) {
      outboundForExport.pickList = [];

      outbound.shipmentLineItems.items.forEach((lineItem) => {
        lineItem.inventoryItems.items.forEach((inventoryItem) => {
          const exportlineItem = {
            inventoryId: lineItem.inventoryId,
            productCode: lineItem.inventory.productCode,
            productName: lineItem.inventory.productName,
            lotNumber: lineItem.lotNumber,
            quantityOutbound: lineItem.totalQuantityToPick,
            units: outbound.totalUOM,
            location: inventoryItem.inventoryItem?.locationFullName || "",
            quantityOnHand: inventoryItem.inventoryItem?.quantityOnHand || "",
          };

          outboundForExport.pickList.push(exportlineItem);
        });
      });
    }
    delete outboundForExport.shipmentLineItems;

    if (outbound.customFields?.length > 0) {
      debugger;
      outbound.customFields.forEach((field) => {
        exportCustomField(field, outboundForExport, customHeaderMap);
      });
    }
    delete outboundForExport.customFields;

    delete outboundForExport.account;
    delete outboundForExport.assignedTo;
    delete outboundForExport.bolFile;
    delete outboundForExport.createdAt;
    delete outboundForExport.createDateTime;
    delete outboundForExport.daysOnDemurrageClock;
    delete outboundForExport.didTarp;
    delete outboundForExport.endDateTime;
    delete outboundForExport.history;
    delete outboundForExport.invoiceId;
    delete outboundForExport.mustTarp;
    delete outboundForExport.reloadYardId;
    delete outboundForExport.startDateTime;
    delete outboundForExport.updatedAt;
    debugger;

    return outboundForExport;
  });

  jsonExport(
    fillDownRows(outboundsForExport),
    { rename: ["outboundShipmentId"] },
    (err, csv) => {
      downloadCSV(csv, `outboundShipments_${new Date().toLocaleDateString()}`);
    },
  );
};
